import {
    Category,
    ClickOffer,
    ListingItem,
    TextBlock,
    TranslationContent,
} from "@lib/shared/types";
import type { Menu } from "src/api";
import {
    PageBuilderBlogPost,
    PageBuilderListings,
    PageBuilderLookupLists,
    RouteConfig,
} from "@page-builder/ts/types";
import getTextBlocks from "pages/api/getTextBlocks";
import { Dispatch, MutableRefObject, SetStateAction } from "react";
import {
    ClicksSearchList,
    ComparisonListing,
    DomainForm,
    DomainInfo,
    DomainPopup,
    getDKIs,
    getDomainForm,
    getGenericRoutes,
    getLookupsAndVariations,
    Listing,
    VisitDetails,
    ZipCodeLocation,
} from "src/api";
import { BlogPostType } from "src/api/getBlog";
import { getListingBySlugV3, ListingV3 } from "src/api/listings";
import { getNearbyZipCodes, ZipCodeInformation } from "src/api/nearbyZipCodes";
import { getMenus } from "src/api/menus";
import { getDomainPosts, GetDomainPostsParams } from "src/api/fetchDomainPosts";
import { DomainPostItemType } from "pages/api/fetchDomainPosts";
import { PiiCompletion } from "src/api/piiCompletion";
import { getUserSession, SessionTestConfig } from "src/api/session";

export type PageType =
    | "home"
    | "listing"
    | "genericRoute"
    | "staticCategory"
    | "category"
    | "comparison"
    | "genericRouteAsCategory"
    | "thankyou"
    | "offer"
    | "claims"
    | "redirect"
    | "alias"
    | "fbLander"
    | "blogPost"
    | "unsubscribe"
    | "do-not-sell"
    | "page-builder"
    | "form-completion"
    | "consent-completion"
    | "auto-sell"
    | "common-offers"
    | "common-offers-generic"
    | "common-offers-generic-as-category"
    | "autoWarranty"
    | "partner-with-us"
    | "ccpa-privacy-notice";

export interface Route {
    id: number;
    route: string;
    isActive: boolean;
    isDeleted: boolean;
    ignoreKeyword: boolean;
    category: Category;
    title: string | null;
}
export interface DomainRoute {
    id: number;
    domainId: number;
    route: string;
    isActive: boolean;
    ignoreKeyword: boolean;
    isDeleted: boolean;
}

export interface Options {
    hasOperatingSystem?: boolean | undefined;
    extraCalls?: ["getLocation"];
    extraProps?: { [x: string]: string | boolean | number | object | [] };
    pageTitle?: string;
    pageDescription?: string;
    getZipCodeLocation?: { zipCode: string };
    getExternalOffersOnThankYouPage?: boolean;
    activateOfferFormTemplate?: boolean;
    getSpecialDomainListing?: boolean;
    lander?: string;
    isStaticContent?: boolean;
    slug?: string;
    fetchForm?: boolean;
    zipCodeFallback?: boolean; // we use this flag in listing page to skip the fallback
    fetchZipCodes?: boolean;
    shouldFetchGeneric?: boolean;
    templateId?: string;
    shouldGetRouteConfig?: boolean;
    categorySlug?: string | null | undefined;
    shouldFetchDki?: boolean;
    removeThankYouOffers?: boolean;
    pageType?: PageType;
    getDomainMenus?: boolean;
}
export type AutoWarrantyMakers = { label: string; value: string; id: number }[];

export type PageProps = {
    domain: DomainInfo;
    visitDetails: VisitDetails;
    testConfig?: SessionTestConfig;
    category?: Category | null;
    form?: DomainForm | null;
    formattedTitle?: string | null;
    clicksOffers?: ClickOffer[] | null;
    ClicksSearchList?: ClicksSearchList | null;
    domainPopup?: DomainPopup | null;
    comparisonListing?: ComparisonListing | null;
    categoryListing?: Listing | null;
    pageTitle: null | string;
    zipCodeLocation?: ZipCodeLocation | null;
    zipCode: string | undefined;
    operatingSystem?: string | null;
    locale: string;
    locales: string[] | null;
    defaultLocale: string | null;
    content: TranslationContent | null;
    error?: { statusCode: 500 };
    userAgent?: string;
    isBot: boolean;
    formTemplate?: string | null | string[];
    formOffer?: ListingItem[] | [];
    exitModalOffer?: ListingItem[] | [];
    thankYouOffers?: ListingItem[] | [];
    headerOffer?: ListingItem[] | [];
    stickyOffer?: ListingItem[] | [];
    thankyouListing?: ListingV3 | null;
    secondListing?: ListingV3 | null;
    blogPost: BlogPostType | null;
    totalTimeSpendOnSite: MutableRefObject<number>;
    initialTimeOnSite: string;
    autoWarrantyMakers: AutoWarrantyMakers;

    routeConfig?: RouteConfig;
    externalFavicon?: string | null;
    pageBuilderBlogPost?: PageBuilderBlogPost;
    pageBuilderLookupLists?: PageBuilderLookupLists;
    pageBuilderListings?: PageBuilderListings;

    piiCompletionData?: PiiCompletion;
    redirectTo?: string;
    disableTimeOnSite?: boolean;
    showFormModal?: boolean;
    setShowFormModalState?: Dispatch<SetStateAction<boolean | undefined>>;
    pageType: string;
    doNotSellFormStates?: { label: string; id: number; value: string }[] | null;
    loadOneToOneForm?: boolean;
    ignoreSticky?: boolean;

    nearbyZipCodes?: ZipCodeInformation | null;
    textBlocks?: TextBlock[] | null;
    taxonomies?: Array<{
        id?: number;
        name: string;
        fields: Array<{
            codeName: string;
            value: string;
        }>;
    }>;
    menus?: Menu[] | null;
    domainPosts?: Record<
        string,
        { data: DomainPostItemType[]; totalCount: number } | null
    >;

    referrer?: string | null;
    defaultFallbackCategory: boolean;
};

export interface IComponentApiCall {
    key: string;
    value:
        | string
        | boolean
        | { categorySlug: string; positions: string[] }
        | Partial<GetDomainPostsParams>;
}

export interface PageDataCalls {
    getDKIs?: ReturnType<typeof getDKIs>;
    getDomainForm?: ReturnType<typeof getDomainForm>;
    getListingBySlugV3?: ReturnType<typeof getListingBySlugV3>[];
    getLookupsAndVariations?: ReturnType<typeof getLookupsAndVariations>[];
    getNearbyZipCodes?: ReturnType<typeof getNearbyZipCodes>;
    getTextBlocks?: ReturnType<typeof getTextBlocks>;
    getMenus?: ReturnType<typeof getMenus>;
    getDomainPosts?: ReturnType<typeof getDomainPosts>[];
}

export enum ApiCallType {
    FORM = "FORM",
    LOOKUP = "LOOKUP",
    LISTING = "LISTING",
    EXTERNAL_LISTING = "EXTERNAL_LISTING",
    DKIS = "DKIS",
    NEARBY_ZIP_CODES = "NEARBY_ZIP_CODES",
    TEXTBLOCKS = "TEXTBLOCKS",
    MENUS = "MENUS",
    DOMAIN_POSTS = "DOMAIN_POSTS",
}

export type PromiseReturnType = ReturnType<
    | typeof getDKIs
    | typeof getDomainForm
    | typeof getLookupsAndVariations
    | typeof getListingBySlugV3
    | typeof getNearbyZipCodes
    | typeof getMenus
    | typeof getDomainPosts
>;
export interface TypedApiCall {
    promise: PromiseReturnType;
    type: ApiCallType;
    identifier: string | boolean; // slug, lookup type, etc.
    componentId?: string; // Original component that requested this
    timestamp: number;
}

export interface InitialCalls {
    getUserSession: ReturnType<typeof getUserSession>;
    getDKIs?: ReturnType<typeof getDKIs>;
    getGenericRoutes?: ReturnType<typeof getGenericRoutes>;
}

export type InitialPromiseArray = [
    Awaited<ReturnType<typeof getUserSession>>?,
    Awaited<ReturnType<typeof getDKIs>>?,
    Awaited<ReturnType<typeof getGenericRoutes>>?,
];
